import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Pause,
  Play,
  Pencil,
  Trash2,
  Eye,
  MousePointer,
  TrendingUp,
  DollarSign,
  Target,
  Calendar,
  Wallet,
  AlertTriangle,
  LayoutGrid,
  Check,
  X,
  Sparkles,
  ImageIcon,
  Store,
  Bell,
  Heart,
  Search,
  ChevronRight,
  Star,
  Package,
  Send,
  CheckCircle2,
} from 'lucide-react';

import { useCampaign } from '../hooks/use-campaign';
import { useCampaignStats } from '../hooks/use-campaign-stats';
import { useCampaignStatus } from '../hooks/use-campaign-status';
import { useDeleteCampaign } from '../hooks/use-delete-campaign';
import { useUpdateCampaign } from '../hooks/use-update-campaign';
import { useAdWallet } from '../hooks/use-ad-wallet';
import type { CampaignStatus, AdType } from '../types/ads.types';
import { NotificationPreview } from '../components/preview/NotificationPreview';
import { Textarea } from '@/components/atoms/textarea';
import { isNotificationType } from '../utils/pricing.utils';
import { getImageUrl } from '@/lib/utils/image';
import { useShops } from '@/features/shops/hooks/use-shops';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Skeleton } from '@/components/atoms/skeleton';
import { MetricCard } from '@/components/organisms/cards';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/molecules/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/molecules/alert-dialog';
import { formatPrice, getAmountByCurrency } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { format } from 'date-fns';

interface CampaignDetailContainerProps {
  campaignId: string;
}

const statusVariantMap: Record<CampaignStatus, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  active: 'default',
  paused: 'secondary',
  completed: 'outline',
  draft: 'outline',
  disabled_by_admin: 'destructive',
  deleted_by_admin: 'destructive',
};

const typeLabels: Record<AdType, string> = {
  sponsored_product: 'ads.types.sponsored_product',
  homepage_carousel: 'ads.types.homepage_carousel',
  featured_shop: 'ads.types.featured_shop',
  category_banner: 'ads.types.category_banner',
  push_all_users: 'ads.types.push_all_users',
  push_shop_subscribers: 'ads.types.push_shop_subscribers',
  push_product_likers: 'ads.types.push_product_likers',
  push_single_product_likers: 'ads.types.push_single_product_likers',
};

export function CampaignDetailContainer({ campaignId }: CampaignDetailContainerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: campaign, isLoading: isLoadingCampaign } = useCampaign(campaignId);
  const { data: stats } = useCampaignStats(campaignId);
  const { data: wallet } = useAdWallet();
  const { data: shops = [] } = useShops();
  const currentShop = shops[0];
  const shopLogo = currentShop?.logo ? getImageUrl(currentShop.logo) : undefined;
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useCampaignStatus();
  const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign();
  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign();
  const { currency } = useConfigStore();

  // Banner text editing state (for category_banner)
  const [isEditingBannerText, setIsEditingBannerText] = React.useState(false);
  const [bannerText, setBannerText] = React.useState('');

  // Headline editing state (for homepage_carousel)
  const [isEditingHeadline, setIsEditingHeadline] = React.useState(false);
  const [headline, setHeadline] = React.useState('');

  // Notification editing state (for push notification types)
  const [isEditingNotification, setIsEditingNotification] = React.useState(false);
  const [notificationTitle, setNotificationTitle] = React.useState('');
  const [notificationMessage, setNotificationMessage] = React.useState('');

  // Sync banner text when campaign loads (for category_banner)
  React.useEffect(() => {
    if (campaign?.items?.[0]?.message) {
      setBannerText(campaign.items[0].message);
    }
  }, [campaign]);

  // Sync headline when campaign loads (for homepage_carousel)
  React.useEffect(() => {
    if (campaign?.items?.[0]?.title) {
      setHeadline(campaign.items[0].title);
    }
  }, [campaign]);

  // Sync notification title/message when campaign loads (for push notifications)
  React.useEffect(() => {
    if (campaign?.items?.[0]) {
      setNotificationTitle(campaign.items[0].title || '');
      setNotificationMessage(campaign.items[0].message || '');
    }
  }, [campaign]);

  const handleSaveBannerText = () => {
    if (!campaign) return;

    const updatedItems = campaign.items?.map((item, index) => {
      if (index === 0) {
        return {
          productRefId: item.productRefId,
          bannerUrl: item.bannerUrl,
          title: bannerText || campaign.name,
          message: bannerText || undefined,
        };
      }
      return {
        productRefId: item.productRefId,
        bannerUrl: item.bannerUrl,
        title: item.title,
        message: item.message,
      };
    });

    updateCampaign(
      { refId: campaignId, data: { items: updatedItems } },
      { onSuccess: () => setIsEditingBannerText(false) }
    );
  };

  const handleCancelEdit = () => {
    setBannerText(campaign?.items?.[0]?.message || '');
    setIsEditingBannerText(false);
  };

  const handleSaveHeadline = () => {
    if (!campaign) return;

    const updatedItems = campaign.items?.map((item, index) => {
      if (index === 0) {
        return {
          productRefId: item.productRefId,
          bannerUrl: item.bannerUrl,
          bannerLink: item.bannerLink,
          title: headline || undefined,
          message: item.message,
        };
      }
      return {
        productRefId: item.productRefId,
        bannerUrl: item.bannerUrl,
        bannerLink: item.bannerLink,
        title: item.title,
        message: item.message,
      };
    });

    updateCampaign(
      { refId: campaignId, data: { items: updatedItems } },
      { onSuccess: () => setIsEditingHeadline(false) }
    );
  };

  const handleCancelHeadlineEdit = () => {
    setHeadline(campaign?.items?.[0]?.title || '');
    setIsEditingHeadline(false);
  };

  const handleSaveNotification = () => {
    if (!campaign) return;

    const updatedItems = campaign.items?.map((item, index) => {
      if (index === 0) {
        return {
          productRefId: item.productRefId,
          bannerUrl: item.bannerUrl,
          title: notificationTitle || undefined,
          message: notificationMessage || undefined,
        };
      }
      return {
        productRefId: item.productRefId,
        bannerUrl: item.bannerUrl,
        title: item.title,
        message: item.message,
      };
    });

    updateCampaign(
      { refId: campaignId, data: { items: updatedItems } },
      { onSuccess: () => setIsEditingNotification(false) }
    );
  };

  const handleCancelNotificationEdit = () => {
    setNotificationTitle(campaign?.items?.[0]?.title || '');
    setNotificationMessage(campaign?.items?.[0]?.message || '');
    setIsEditingNotification(false);
  };

  // Calculate required balance for activation (budget - already spent)
  const requiredBalance = campaign
    ? getAmountByCurrency(currency, {
        eur: campaign.budgetEur - campaign.spentEur,
        usd: campaign.budgetUsd - campaign.spentUsd,
        kmf: campaign.budgetKmf - campaign.spentKmf,
      })
    : 0;

  const currentBalance = wallet
    ? getAmountByCurrency(currency, {
        eur: wallet.balanceEur,
        usd: wallet.balanceUsd,
        kmf: wallet.balanceKmf,
      })
    : 0;

  const hasInsufficientBalance = currentBalance < requiredBalance;

  const handleToggleStatus = () => {
    if (!campaign) return;
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    updateStatus({ refId: campaignId, status: newStatus });
  };

  const handleDelete = () => {
    deleteCampaign(campaignId, {
      onSuccess: () => navigate('/ads/campaigns'),
    });
  };

  if (isLoadingCampaign) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-16 w-full" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-muted-foreground">{t('ads.campaigns.notFound')}</p>
        <Button variant="outline" className="mt-4" asChild>
          <Link to="/ads/campaigns">{t('common.back')}</Link>
        </Button>
      </div>
    );
  }

  const progress = campaign.budgetEur > 0 ? (campaign.spentEur / campaign.budgetEur) * 100 : 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Compact on mobile */}
      <div className="space-y-3 sm:space-y-4">
        {/* Title row */}
        <div className="flex items-center gap-2 sm:gap-4">
          <Button variant="ghost" size="icon" className="size-8 sm:size-10 shrink-0" asChild>
            <Link to="/ads/campaigns">
              <ArrowLeft className="size-4" />
            </Link>
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-base sm:text-2xl font-bold truncate">{campaign.name}</h1>
              <Badge variant={statusVariantMap[campaign.status]} className="shrink-0 text-xs">
                {t(`ads.status.${campaign.status}`)}
              </Badge>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">
              {t(typeLabels[campaign.type])}
              <span className="hidden sm:inline"> • {t('ads.campaigns.createdAt')} {format(new Date(campaign.createdAt), 'PPP')}</span>
            </p>
          </div>
        </div>

        {/* Balance info for DRAFT campaigns - Full width card on mobile */}
        {campaign.status === 'draft' && (
          <div className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 rounded-xl border ${hasInsufficientBalance ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800' : 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800'}`}>
            <div className="flex items-center gap-3 flex-1">
              <div className={`shrink-0 p-2 rounded-lg ${hasInsufficientBalance ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                <Wallet className={`size-5 ${hasInsufficientBalance ? 'text-red-600' : 'text-green-600'}`} />
              </div>
              <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-muted-foreground">{t('ads.campaigns.walletBalance')}</span>
                <span className={`font-semibold text-right ${hasInsufficientBalance ? 'text-red-600' : 'text-green-600'}`}>
                  {formatPrice(currentBalance, currency)}
                </span>
                <span className="text-muted-foreground">{t('ads.campaigns.requiredBalance')}</span>
                <span className="font-semibold text-right">{formatPrice(requiredBalance, currency)}</span>
              </div>
              {hasInsufficientBalance && (
                <AlertTriangle className="size-5 text-red-600 shrink-0" />
              )}
            </div>
            <Button
              variant="default"
              size="lg"
              className="w-full sm:w-auto"
              onClick={handleToggleStatus}
              disabled={isUpdatingStatus || hasInsufficientBalance}
            >
              <Play className="size-4 mr-2" />
              {t('ads.campaigns.activate')}
            </Button>
          </div>
        )}

        {/* Action buttons - Icons only on mobile */}
        <div className="flex items-center gap-2">
          {/* Show pause/resume for active/paused campaigns */}
          {(campaign.status === 'active' || campaign.status === 'paused') && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleStatus}
              disabled={isUpdatingStatus}
              className="gap-1.5"
            >
              {campaign.status === 'active' ? (
                <>
                  <Pause className="size-4" />
                  <span className="hidden sm:inline">{t('ads.campaigns.pause')}</span>
                </>
              ) : (
                <>
                  <Play className="size-4" />
                  <span className="hidden sm:inline">{t('ads.campaigns.resume')}</span>
                </>
              )}
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5" asChild>
            <Link to={`/ads/campaigns/${campaignId}/edit`}>
              <Pencil className="size-4" />
              <span className="hidden sm:inline">{t('common.edit')}</span>
            </Link>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="gap-1.5">
                <Trash2 className="size-4" />
                <span className="hidden sm:inline">{t('common.delete')}</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('ads.campaigns.deleteTitle')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('ads.campaigns.deleteDescription')}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {isDeleting ? t('common.loading') : t('common.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Stats Cards - Horizontal scroll on mobile */}
      {/* Show different stats for notifications vs visibility campaigns */}
      {isNotificationType(campaign.type) ? (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible scrollbar-hide">
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.notifications.sent', 'Envoyées')}
              value={(stats?.impressions ?? campaign.impressions ?? 0).toLocaleString()}
              icon={Send}
            />
          </div>
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.notifications.delivered', 'Délivrées')}
              value={(stats?.impressions ?? campaign.impressions ?? 0).toLocaleString()}
              icon={CheckCircle2}
            />
          </div>
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.notifications.opened', 'Ouvertes')}
              value={(stats?.clicks ?? campaign.clicks ?? 0).toLocaleString()}
              icon={Eye}
            />
          </div>
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.notifications.openRate', "Taux d'ouverture")}
              value={`${(stats?.ctr ?? campaign.ctr ?? 0).toFixed(2)}%`}
              icon={TrendingUp}
            />
          </div>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible scrollbar-hide">
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.impressions')}
              value={(stats?.impressions ?? campaign.impressions ?? 0).toLocaleString()}
              icon={Eye}
            />
          </div>
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.clicks')}
              value={(stats?.clicks ?? campaign.clicks ?? 0).toLocaleString()}
              icon={MousePointer}
            />
          </div>
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.ctr')}
              value={`${(stats?.ctr ?? campaign.ctr ?? 0).toFixed(2)}%`}
              icon={TrendingUp}
            />
          </div>
          <div className="shrink-0 w-[140px] sm:w-auto">
            <MetricCard
              title={t('ads.conversions')}
              value={(stats?.conversions ?? campaign.conversions ?? 0).toLocaleString()}
              icon={Target}
            />
          </div>
        </div>
      )}

      {/* Campaign Preview Section */}
      <CampaignPreview
        campaign={campaign}
        shopLogo={shopLogo}
        isEditingBannerText={isEditingBannerText}
        setIsEditingBannerText={setIsEditingBannerText}
        bannerText={bannerText}
        setBannerText={setBannerText}
        handleSaveBannerText={handleSaveBannerText}
        handleCancelEdit={handleCancelEdit}
        isEditingHeadline={isEditingHeadline}
        setIsEditingHeadline={setIsEditingHeadline}
        headline={headline}
        setHeadline={setHeadline}
        handleSaveHeadline={handleSaveHeadline}
        handleCancelHeadlineEdit={handleCancelHeadlineEdit}
        isEditingNotification={isEditingNotification}
        setIsEditingNotification={setIsEditingNotification}
        notificationTitle={notificationTitle}
        setNotificationTitle={setNotificationTitle}
        notificationMessage={notificationMessage}
        setNotificationMessage={setNotificationMessage}
        handleSaveNotification={handleSaveNotification}
        handleCancelNotificationEdit={handleCancelNotificationEdit}
        isUpdating={isUpdating}
      />

      {/* Budget & Performance - Tabs on mobile, Grid on desktop */}
      {/* Mobile: Tabs */}
      <div className="sm:hidden">
        <Tabs defaultValue="budget">
          <TabsList className="w-full">
            <TabsTrigger value="budget" className="flex-1 gap-1.5">
              <DollarSign className="size-4" />
              Budget
            </TabsTrigger>
            <TabsTrigger value="schedule" className="flex-1 gap-1.5">
              <Calendar className="size-4" />
              Période
            </TabsTrigger>
          </TabsList>
          <TabsContent value="budget" className="mt-3">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('ads.spent')}</span>
                    <span className="font-medium">
                      {formatPrice(getAmountByCurrency(currency, { eur: campaign.spentEur, usd: campaign.spentUsd, kmf: campaign.spentKmf }), currency)} / {formatPrice(getAmountByCurrency(currency, { eur: campaign.budgetEur, usd: campaign.budgetUsd, kmf: campaign.budgetKmf }), currency)}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(progress, 100)}%` }} />
                  </div>
                  <p className="text-xs text-muted-foreground text-right">{progress.toFixed(1)}% utilisé</p>
                </div>
                {campaign.dailyBudgetEur && (
                  <div className="flex justify-between py-2 border-t text-sm">
                    <span className="text-muted-foreground">Limite/jour</span>
                    <span className="font-medium">{formatPrice(getAmountByCurrency(currency, { eur: campaign.dailyBudgetEur, usd: campaign.dailyBudgetUsd, kmf: campaign.dailyBudgetKmf }), currency)}</span>
                  </div>
                )}
                {campaign.bidAmountEur && (
                  <div className="flex justify-between py-2 border-t text-sm">
                    <span className="text-muted-foreground">Enchère/clic</span>
                    <span className="font-medium">{formatPrice(getAmountByCurrency(currency, { eur: campaign.bidAmountEur, usd: campaign.bidAmountUsd, kmf: campaign.bidAmountKmf }), currency)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="schedule" className="mt-3">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-muted-foreground">Début</span>
                  <span className="font-medium">{format(new Date(campaign.startDate), 'dd/MM/yyyy HH:mm')} (UTC)</span>
                </div>
                <div className="flex justify-between py-2 border-t text-sm">
                  <span className="text-muted-foreground">Fin</span>
                  <span className="font-medium">{format(new Date(campaign.endDate), 'dd/MM/yyyy HH:mm')} (UTC)</span>
                </div>
                {campaign.targeting?.countryIds && campaign.targeting.countryIds.length > 0 && (
                  <div className="py-2 border-t">
                    <span className="text-muted-foreground text-sm block mb-2">Pays</span>
                    <div className="flex flex-wrap gap-1">
                      {campaign.targeting.countryIds.map((countryId) => (
                        <Badge key={countryId} variant="secondary" className="text-xs">{countryId}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Desktop: Grid */}
      <div className="hidden sm:grid gap-6 md:grid-cols-2">
        {/* Budget Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="size-5" />
              {t('ads.campaigns.budgetOverview')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t('ads.spent')}</span>
                <span className="font-medium">
                  {formatPrice(getAmountByCurrency(currency, { eur: campaign.spentEur, usd: campaign.spentUsd, kmf: campaign.spentKmf }), currency)} / {formatPrice(getAmountByCurrency(currency, { eur: campaign.budgetEur, usd: campaign.budgetUsd, kmf: campaign.budgetKmf }), currency)}
                </span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${Math.min(progress, 100)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {progress.toFixed(1)}% {t('ads.campaigns.used')}
              </p>
            </div>

            {campaign.dailyBudgetEur && (
              <div className="flex justify-between py-2 border-t">
                <span className="text-muted-foreground">{t('ads.campaigns.form.dailyBudget')}</span>
                <span className="font-medium">{formatPrice(getAmountByCurrency(currency, { eur: campaign.dailyBudgetEur, usd: campaign.dailyBudgetUsd, kmf: campaign.dailyBudgetKmf }), currency)}</span>
              </div>
            )}

            {campaign.bidAmountEur && (
              <div className="flex justify-between py-2 border-t">
                <span className="text-muted-foreground">{t('ads.campaigns.form.bidAmount')}</span>
                <span className="font-medium">{formatPrice(getAmountByCurrency(currency, { eur: campaign.bidAmountEur, usd: campaign.bidAmountUsd, kmf: campaign.bidAmountKmf }), currency)}</span>
              </div>
            )}

            {stats && (
              <div className="flex justify-between py-2 border-t">
                <span className="text-muted-foreground">{t('ads.campaigns.avgCpc')}</span>
                <span className="font-medium">{formatPrice(getAmountByCurrency(currency, { eur: stats.avgCpcEur, usd: stats.avgCpcUsd, kmf: stats.avgCpcKmf }), currency)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Schedule Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="size-5" />
              {t('ads.campaigns.schedule')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">{t('ads.campaigns.form.startDate')}</span>
              <span className="font-medium">{format(new Date(campaign.startDate), 'PPP')} à {format(new Date(campaign.startDate), 'HH:mm')} (UTC)</span>
            </div>
            <div className="flex justify-between py-2 border-t">
              <span className="text-muted-foreground">{t('ads.campaigns.form.endDate')}</span>
              <span className="font-medium">{format(new Date(campaign.endDate), 'PPP')} à {format(new Date(campaign.endDate), 'HH:mm')} (UTC)</span>
            </div>

            {campaign.targeting && (
              <>
                {campaign.targeting.countryIds && campaign.targeting.countryIds.length > 0 && (
                  <div className="py-2 border-t">
                    <span className="text-muted-foreground block mb-2">{t('ads.campaigns.targeting.countries')}</span>
                    <div className="flex flex-wrap gap-1">
                      {campaign.targeting.countryIds.map((countryId) => (
                        <Badge key={countryId} variant="secondary">{countryId}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {campaign.targeting.genders && campaign.targeting.genders.length > 0 && (
                  <div className="flex justify-between py-2 border-t">
                    <span className="text-muted-foreground">{t('ads.campaigns.targeting.gender')}</span>
                    <span className="font-medium">
                      {campaign.targeting.genders.map((g) => t(`ads.campaigns.targeting.${g}`)).join(', ')}
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Campaign Items */}
      {campaign.items && campaign.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('ads.campaigns.items')}</CardTitle>
            <CardDescription>{t('ads.campaigns.itemsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {campaign.items.map((item, index) => (
                <div
                  key={item.id || index}
                  className={`flex items-center justify-between p-3 bg-muted rounded-lg transition-colors ${
                    item.productRefId ? 'cursor-pointer hover:bg-muted/80' : ''
                  }`}
                  onClick={() => {
                    if (item.productRefId) {
                      navigate(`/products/${item.productRefId}`);
                    }
                  }}
                >
                  <div>
                    {item.productName && <p className="font-medium">{item.productName}</p>}
                    {item.title && <p className="font-medium">{item.title}</p>}
                    {item.message && <p className="text-sm text-muted-foreground">{item.message}</p>}
                  </div>
                  {(item.bannerUrl || item.productImageUrl) && (
                    <img src={item.bannerUrl || item.productImageUrl} alt="" className="h-12 w-20 object-cover rounded" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============ Campaign Preview Component ============

interface CampaignPreviewProps {
  campaign: NonNullable<ReturnType<typeof useCampaign>['data']>;
  shopLogo?: string;
  isEditingBannerText: boolean;
  setIsEditingBannerText: (value: boolean) => void;
  bannerText: string;
  setBannerText: (value: string) => void;
  handleSaveBannerText: () => void;
  handleCancelEdit: () => void;
  isEditingHeadline: boolean;
  setIsEditingHeadline: (value: boolean) => void;
  headline: string;
  setHeadline: (value: string) => void;
  handleSaveHeadline: () => void;
  handleCancelHeadlineEdit: () => void;
  isEditingNotification: boolean;
  setIsEditingNotification: (value: boolean) => void;
  notificationTitle: string;
  setNotificationTitle: (value: string) => void;
  notificationMessage: string;
  setNotificationMessage: (value: string) => void;
  handleSaveNotification: () => void;
  handleCancelNotificationEdit: () => void;
  isUpdating: boolean;
}

function CampaignPreview({
  campaign,
  shopLogo,
  isEditingBannerText,
  setIsEditingBannerText,
  bannerText,
  setBannerText,
  handleSaveBannerText,
  handleCancelEdit,
  isEditingHeadline,
  setIsEditingHeadline,
  headline,
  setHeadline,
  handleSaveHeadline,
  handleCancelHeadlineEdit,
  isEditingNotification,
  setIsEditingNotification,
  notificationTitle,
  setNotificationTitle,
  notificationMessage,
  setNotificationMessage,
  handleSaveNotification,
  handleCancelNotificationEdit,
  isUpdating,
}: CampaignPreviewProps) {
  const isNotification = isNotificationType(campaign.type);

  // Get preview config based on campaign type
  const previewConfig = getPreviewConfig(campaign.type);
  const firstItem = campaign.items?.[0];
  const productImage: string | null = firstItem?.bannerUrl ?? firstItem?.productImageUrl ?? null;
  const productName = firstItem?.productName || firstItem?.title || campaign.name;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {previewConfig.icon}
          {previewConfig.title}
        </CardTitle>
        <CardDescription>{previewConfig.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Mobile Device Mockup */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              {/* Phone frame */}
              <div className="w-[280px] bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
                <div className="bg-gray-900 rounded-[2rem] overflow-hidden">
                  {/* Status bar */}
                  <div className="bg-gray-900 px-6 py-2 flex items-center justify-between text-white text-xs">
                    <span>9:41</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 border border-white rounded-sm">
                        <div className="w-2/3 h-full bg-white rounded-sm" />
                      </div>
                    </div>
                  </div>

                  {/* App content - varies by type */}
                  <div className="bg-white min-h-[400px]">
                    {campaign.type === 'sponsored_product' && (
                      <SponsoredProductPreview
                        productImage={productImage}
                        productName={productName}
                      />
                    )}

                    {campaign.type === 'homepage_carousel' && (
                      <HomepageCarouselPreview
                        productImage={productImage}
                        productName={productName}
                        headline={isEditingHeadline ? headline : (firstItem?.title || '')}
                        shopName={campaign.shopName}
                      />
                    )}

                    {campaign.type === 'featured_shop' && (
                      <FeaturedShopPreview
                        shopName={campaign.shopName}
                        shopLogo={firstItem?.bannerUrl}
                      />
                    )}

                    {campaign.type === 'category_banner' && (
                      <CategoryBannerPreview
                        bannerUrl={firstItem?.bannerUrl}
                        bannerText={isEditingBannerText ? bannerText : (firstItem?.message || '')}
                      />
                    )}

                    {isNotification && (
                      <div className="h-full flex items-center justify-center p-4 bg-gradient-to-b from-blue-400 to-purple-500">
                        <NotificationPreview
                          shopName={campaign.shopName || 'Ma Boutique'}
                          shopLogo={shopLogo}
                          title={isEditingNotification ? notificationTitle : (firstItem?.title || '')}
                          message={isEditingNotification ? notificationMessage : (firstItem?.message || '')}
                          image={productImage || undefined}
                          className="transform scale-90"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Device label */}
              <div className="text-center mt-3">
                <Badge variant="outline" className="text-xs">
                  📱 Aperçu mobile
                </Badge>
              </div>
            </div>
          </div>

          {/* Preview Info */}
          <div className="flex-1 space-y-4">
            {/* Headline editor - for homepage_carousel */}
            {campaign.type === 'homepage_carousel' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Texte promotionnel</h4>
                  {!isEditingHeadline && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingHeadline(true)}
                    >
                      <Pencil className="size-4 mr-1" />
                      Modifier
                    </Button>
                  )}
                </div>

                {isEditingHeadline ? (
                  <div className="space-y-3">
                    <Input
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value.slice(0, 40))}
                      placeholder="Ex: -50% sur tout le magasin !"
                      maxLength={40}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{headline.length}/40 caractères</span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelHeadlineEdit}
                          disabled={isUpdating}
                        >
                          <X className="size-4 mr-1" />
                          Annuler
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveHeadline}
                          disabled={isUpdating}
                        >
                          <Check className="size-4 mr-1" />
                          {isUpdating ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-muted rounded-lg">
                    {firstItem?.title ? (
                      <p className="text-sm font-medium line-clamp-2">« {firstItem.title} »</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Aucun texte défini</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Banner text editor - only for category_banner */}
            {campaign.type === 'category_banner' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Texte de la bannière</h4>
                  {!isEditingBannerText && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingBannerText(true)}
                    >
                      <Pencil className="size-4 mr-1" />
                      Modifier
                    </Button>
                  )}
                </div>

                {isEditingBannerText ? (
                  <div className="space-y-3">
                    <Input
                      value={bannerText}
                      onChange={(e) => setBannerText(e.target.value.slice(0, 50))}
                      placeholder="Ex: -20% sur toute la collection !"
                      maxLength={50}
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{bannerText.length}/50 caractères</span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                        >
                          <X className="size-4 mr-1" />
                          Annuler
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveBannerText}
                          disabled={isUpdating}
                        >
                          <Check className="size-4 mr-1" />
                          {isUpdating ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-muted rounded-lg">
                    {firstItem?.message ? (
                      <p className="text-sm font-medium line-clamp-2">"{firstItem.message}"</p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">Aucun texte défini</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Campaign type specific details */}
            <div>
              <h4 className="font-semibold mb-3">Détails de l'affichage</h4>
              <div className="space-y-2">
                {previewConfig.details.map((detail, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm text-muted-foreground">{detail.label}</span>
                    <span className="text-sm font-medium">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notification title & message editor */}
            {isNotification && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">📝 Contenu de la notification</h4>
                  {!isEditingNotification && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingNotification(true)}
                    >
                      <Pencil className="size-4 mr-1" />
                      Modifier
                    </Button>
                  )}
                </div>

                {isEditingNotification ? (
                  <div className="space-y-4 p-4 bg-muted/50 rounded-xl border">
                    {/* Title input */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Titre (optionnel)
                      </label>
                      <Input
                        value={notificationTitle}
                        onChange={(e) => setNotificationTitle(e.target.value.slice(0, 50))}
                        placeholder="Ex: Nouvelle collection !"
                        maxLength={50}
                      />
                      <span className="text-xs text-muted-foreground">{notificationTitle.length}/50 caractères</span>
                    </div>

                    {/* Message textarea */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Message *
                      </label>
                      <Textarea
                        value={notificationMessage}
                        onChange={(e) => setNotificationMessage(e.target.value.slice(0, 200))}
                        placeholder="Votre message de notification..."
                        maxLength={200}
                        rows={3}
                      />
                      <span className="text-xs text-muted-foreground">{notificationMessage.length}/200 caractères</span>
                    </div>

                    {/* Action buttons */}
                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelNotificationEdit}
                        disabled={isUpdating}
                      >
                        <X className="size-4 mr-1" />
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveNotification}
                        disabled={isUpdating || !notificationMessage.trim()}
                      >
                        <Check className="size-4 mr-1" />
                        {isUpdating ? 'Enregistrement...' : 'Enregistrer'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Title preview */}
                    <div className="p-3 bg-muted rounded-lg">
                      <span className="text-xs text-muted-foreground block mb-1">Titre</span>
                      {firstItem?.title ? (
                        <p className="text-sm font-semibold">{firstItem.title}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">Aucun titre défini</p>
                      )}
                    </div>

                    {/* Message preview */}
                    <div className="p-3 bg-muted rounded-lg">
                      <span className="text-xs text-muted-foreground block mb-1">Message</span>
                      {firstItem?.message ? (
                        <p className="text-sm">"{firstItem.message}"</p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic">Aucun message défini</p>
                      )}
                    </div>

                    {/* Product info for product-related notifications */}
                    {(campaign.type === 'push_product_likers' || campaign.type === 'push_single_product_likers') && productImage && (
                      <div className="p-3 bg-muted rounded-lg">
                        <span className="text-xs text-muted-foreground block mb-2">Produit associé</span>
                        <div className="flex items-center gap-3">
                          <img src={productImage} alt="" className="size-12 rounded-lg object-cover" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{productName}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <Heart className="size-3 text-red-500 fill-red-500" />
                              Fans de ce produit
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Full size preview for banner types */}
            {(campaign.type === 'category_banner' || campaign.type === 'homepage_carousel') && productImage && (
              <div>
                <h4 className="font-semibold mb-3">Aperçu agrandi</h4>
                <div className="relative rounded-xl overflow-hidden border bg-muted/50">
                  <img
                    src={productImage}
                    alt="Aperçu"
                    className="w-full h-auto max-h-48 object-cover"
                  />
                  {campaign.type === 'category_banner' && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
                      <div className="absolute inset-0 flex items-center p-6">
                        <div className="text-white max-w-[60%]">
                          {(isEditingBannerText ? bannerText : firstItem?.message) ? (
                            <p className="text-xl font-bold drop-shadow-lg leading-tight line-clamp-2">
                              {isEditingBannerText ? bannerText : firstItem?.message}
                            </p>
                          ) : (
                            <p className="text-lg text-white/60 italic">Aucun texte</p>
                          )}
                          <p className="text-sm mt-2 opacity-80">Cliquez pour découvrir →</p>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="absolute bottom-2 right-2">
                    <span className="text-xs bg-black/60 text-white px-2 py-0.5 rounded">AD</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Preview Sub-Components ============

function SponsoredProductPreview({ productImage, productName }: { productImage: string | null; productName: string }) {
  return (
    <div className="h-full">
      {/* Search bar */}
      <div className="bg-primary px-3 py-2">
        <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1.5">
          <Search className="size-3 text-white/70" />
          <span className="text-xs text-white/70">Rechercher...</span>
        </div>
      </div>

      {/* Search results */}
      <div className="p-2 space-y-2">
        <p className="text-xs text-muted-foreground px-1">Résultats de recherche</p>

        {/* Sponsored product - highlighted */}
        <div className="relative bg-amber-50 border-2 border-amber-200 rounded-lg p-2">
          <div className="absolute -top-2 left-2">
            <span className="text-[9px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5">
              <Sparkles className="size-2" />
              Sponsorisé
            </span>
          </div>
          <div className="flex gap-2 mt-1">
            {productImage ? (
              <img src={productImage} alt="" className="size-16 rounded object-cover" />
            ) : (
              <div className="size-16 rounded bg-gray-200 flex items-center justify-center">
                <Package className="size-6 text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium line-clamp-2">{productName}</p>
              <p className="text-xs text-primary font-bold mt-1">12 500 KMF</p>
              <div className="flex items-center gap-0.5 mt-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="size-2 fill-amber-400 text-amber-400" />
                ))}
                <span className="text-[9px] text-muted-foreground ml-0.5">(24)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Regular products */}
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border rounded-lg p-2 opacity-60">
            <div className="flex gap-2">
              <div className="size-12 rounded bg-gray-200" />
              <div className="flex-1">
                <div className="h-2 bg-gray-200 rounded w-3/4 mb-1" />
                <div className="h-2 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomepageCarouselPreview({ productImage, productName, headline, shopName }: { productImage: string | null; productName: string; headline?: string; shopName?: string }) {
  return (
    <div className="h-full">
      {/* App header */}
      <div className="bg-primary px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-white font-bold text-sm">Volovolo</span>
          <div className="flex items-center gap-2">
            <div className="size-5 bg-white/20 rounded-full" />
          </div>
        </div>
      </div>

      {/* Carousel */}
      <div className="p-2">
        <div className="relative rounded-xl overflow-hidden shadow-lg">
          {productImage ? (
            <img src={productImage} alt="" className="w-full h-32 object-cover" />
          ) : (
            <div className="w-full h-32 bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
              <ImageIcon className="size-10 text-primary/50" />
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            {headline && (
              <p className="text-white text-xs font-bold line-clamp-1 mb-0.5">{headline}</p>
            )}
            <div className="flex items-center gap-1 text-white/90">
              <Store className="size-3" />
              <span className="text-[10px] font-normal">{shopName || productName}</span>
            </div>
          </div>
          {/* AD badge */}
          <div className="absolute top-2 right-2">
            <span className="text-[8px] bg-black/50 text-white px-1.5 py-0.5 rounded">AD</span>
          </div>
          {/* Carousel indicators */}
          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
            <div className="w-4 h-1 bg-white rounded-full" />
            <div className="w-1 h-1 bg-white/50 rounded-full" />
            <div className="w-1 h-1 bg-white/50 rounded-full" />
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="px-2 pt-2">
        <p className="text-xs font-semibold mb-2">Catégories</p>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['Mode', 'Tech', 'Maison', 'Sport'].map((cat) => (
            <div key={cat} className="shrink-0 px-3 py-1.5 bg-gray-100 rounded-full">
              <span className="text-[10px] font-medium">{cat}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Products grid */}
      <div className="p-2 grid grid-cols-2 gap-2">
        {[1, 2].map((i) => (
          <div key={i} className="bg-gray-100 rounded-lg p-2">
            <div className="bg-gray-200 rounded aspect-square mb-1" />
            <div className="h-2 bg-gray-200 rounded w-3/4 mb-1" />
            <div className="h-2 bg-gray-300 rounded w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function FeaturedShopPreview({ shopName, shopLogo }: { shopName: string; shopLogo?: string }) {
  return (
    <div className="h-full">
      {/* App header */}
      <div className="bg-primary px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-white font-bold text-sm">Boutiques</span>
          <Search className="size-4 text-white/70" />
        </div>
      </div>

      {/* Featured shop section */}
      <div className="p-2">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold">En vedette</p>
          <ChevronRight className="size-4 text-muted-foreground" />
        </div>

        {/* Featured shop card - highlighted */}
        <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-3 shadow-md">
          <div className="absolute -top-2 -right-1">
            <span className="text-[9px] bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full font-bold flex items-center gap-0.5 shadow">
              <Star className="size-2 fill-white" />
              En vedette
            </span>
          </div>
          <div className="flex items-center gap-3">
            {shopLogo ? (
              <img src={shopLogo} alt="" className="size-14 rounded-xl object-cover ring-2 ring-amber-200" />
            ) : (
              <div className="size-14 rounded-xl bg-white flex items-center justify-center ring-2 ring-amber-200">
                <Store className="size-6 text-amber-500" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{shopName}</p>
              <div className="flex items-center gap-1 mt-0.5">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="size-2.5 fill-amber-400 text-amber-400" />
                ))}
                <span className="text-[10px] text-muted-foreground ml-0.5">4.8</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">+150 produits</p>
            </div>
            <ChevronRight className="size-5 text-amber-500" />
          </div>
        </div>
      </div>

      {/* Regular shops */}
      <div className="px-2 pt-2">
        <p className="text-xs font-semibold mb-2">Autres boutiques</p>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg opacity-60">
              <div className="size-10 rounded-lg bg-gray-200" />
              <div className="flex-1">
                <div className="h-2.5 bg-gray-200 rounded w-2/3 mb-1" />
                <div className="h-2 bg-gray-100 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CategoryBannerPreview({ bannerUrl, bannerText }: { bannerUrl?: string; bannerText: string }) {
  return (
    <div className="h-full">
      {/* Category header */}
      <div className="bg-primary px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-white/20 rounded" />
          <span className="text-white font-medium text-sm">Catégorie</span>
        </div>
      </div>

      {/* Banner placement */}
      <div className="p-2 bg-gray-50">
        <div className="relative rounded-lg overflow-hidden shadow-md">
          {bannerUrl ? (
            <img src={bannerUrl} alt="" className="w-full h-24 object-cover" />
          ) : (
            <div className="w-full h-24 bg-gradient-to-r from-primary/30 to-primary/10" />
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
          {/* Text overlay */}
          {bannerText && (
            <div className="absolute inset-0 flex items-center px-3">
              <p className="text-white text-xs font-bold drop-shadow-lg leading-tight max-w-[70%] line-clamp-2">
                {bannerText}
              </p>
            </div>
          )}
          <div className="absolute bottom-1 right-1">
            <span className="text-[8px] bg-black/50 text-white px-1 rounded">AD</span>
          </div>
        </div>
      </div>

      {/* Simulated products grid */}
      <div className="p-2 grid grid-cols-2 gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-gray-100 rounded-lg p-2">
            <div className="bg-gray-200 rounded aspect-square mb-2" />
            <div className="h-2 bg-gray-200 rounded w-3/4 mb-1" />
            <div className="h-2 bg-gray-300 rounded w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ Preview Config Helper ============

function getPreviewConfig(type: AdType): {
  icon: React.ReactNode;
  title: string;
  description: string;
  details: { label: string; value: string }[];
} {
  const configs: Record<AdType, ReturnType<typeof getPreviewConfig>> = {
    sponsored_product: {
      icon: <Sparkles className="size-5 text-amber-500" />,
      title: 'Aperçu - Produit sponsorisé',
      description: 'Votre produit apparaît en premier dans les résultats de recherche avec un badge "Sponsorisé"',
      details: [
        { label: 'Emplacement', value: 'Résultats de recherche' },
        { label: 'Position', value: 'En tête de liste' },
        { label: 'Badge', value: 'Sponsorisé' },
      ],
    },
    homepage_carousel: {
      icon: <ImageIcon className="size-5 text-blue-500" />,
      title: 'Aperçu - Carousel accueil',
      description: 'Votre produit apparaît dans le carousel de la page d\'accueil',
      details: [
        { label: 'Emplacement', value: 'Page d\'accueil' },
        { label: 'Format', value: 'Carousel défilant' },
        { label: 'Visibilité', value: 'Tous les utilisateurs' },
      ],
    },
    featured_shop: {
      icon: <Store className="size-5 text-orange-500" />,
      title: 'Aperçu - Boutique en vedette',
      description: 'Votre boutique est mise en avant avec un badge spécial',
      details: [
        { label: 'Emplacement', value: 'Page des boutiques' },
        { label: 'Position', value: 'Section "En vedette"' },
        { label: 'Badge', value: 'En vedette + étoile' },
      ],
    },
    category_banner: {
      icon: <LayoutGrid className="size-5 text-purple-500" />,
      title: 'Aperçu - Bannière catégorie',
      description: 'Votre bannière apparaît en haut de la page catégorie',
      details: [
        { label: 'Emplacement', value: 'Haut de catégorie' },
        { label: 'Format', value: 'Bannière avec texte' },
        { label: 'Visibilité', value: 'Visiteurs de la catégorie' },
      ],
    },
    push_all_users: {
      icon: <Bell className="size-5 text-red-500" />,
      title: 'Aperçu - Notification push',
      description: 'Notification envoyée à tous les utilisateurs de l\'application',
      details: [
        { label: 'Type', value: 'Push notification' },
        { label: 'Audience', value: 'Tous les utilisateurs' },
        { label: 'Appareil', value: 'Mobile (iOS & Android)' },
      ],
    },
    push_shop_subscribers: {
      icon: <Bell className="size-5 text-green-500" />,
      title: 'Aperçu - Notification abonnés',
      description: 'Notification envoyée à tous les abonnés de votre boutique',
      details: [
        { label: 'Type', value: 'Push notification' },
        { label: 'Audience', value: 'Abonnés boutique' },
        { label: 'Appareil', value: 'Mobile (iOS & Android)' },
      ],
    },
    push_product_likers: {
      icon: <Heart className="size-5 text-pink-500" />,
      title: 'Aperçu - Notification fans produits',
      description: 'Notification envoyée aux personnes qui ont aimé vos produits',
      details: [
        { label: 'Type', value: 'Push notification' },
        { label: 'Audience', value: 'Fans de vos produits' },
        { label: 'Appareil', value: 'Mobile (iOS & Android)' },
      ],
    },
    push_single_product_likers: {
      icon: <Heart className="size-5 text-red-500" />,
      title: 'Aperçu - Notification fans d\'un produit',
      description: 'Notification envoyée aux personnes qui ont aimé ce produit spécifique',
      details: [
        { label: 'Type', value: 'Push notification' },
        { label: 'Audience', value: 'Fans du produit' },
        { label: 'Appareil', value: 'Mobile (iOS & Android)' },
      ],
    },
  };

  return configs[type];
}
